import React, { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import {useTranslation} from 'react-i18next';
import { PerspectiveCamera, useContextBridge } from '@react-three/drei';
import {GroupProps} from '@react-three/fiber/dist/declarations/src/three-types';

import CameraController from './CameraController';

import { AssetContext } from '../../context/AssetContext';
import { AudioContext } from '../../context/AudioContext';
import { DetailViewContext, useDetailViewContext } from '../../context/DetailViewContext';
import { FocusContext } from './context/FocusContext';
import { useNavigationContext } from '../../context/NavigationContext';
import { DetailSectionIDs } from '../../data/Sections';
import FocusPoints from '../../data/FocusPoints';
import useWindowSize from '../../hooks/useWindowSize';

import VectorRepresentation from './sections/VectorRepresentation';
import { SectionPositions } from './SectionPosition';
import SphereSubspaces from './sections/SphereSubspaces';
import TopologicalRelations from './sections/TopologicalRelations';
import DynamicBehavior from './sections/DynamicBehavior';

import ForwardEvents from './ForwardEvents';
import BackgroundSphere from './BackgroundSphere';
import RenderConditionally from './RenderConditionally';

import styles from './styles.module.scss';

export type SceneSection = {
  groupProps: GroupProps,
  texts: any
}

export default function View3D(): JSX.Element {
  const windowSize = useWindowSize();
  const { isMenuOpen } = useNavigationContext();
  const { advancedModeOn } = useDetailViewContext();

  // this is needed to allow ThreeJS components to use the app's contexts
  const AnimationContextBridge = useContextBridge(
    AudioContext, DetailViewContext, AssetContext, FocusContext
  );

  const {
    detailViewVisible,
    activeDetailID,
    activeFocusID,
    showExpectationBox,
    showRealityBox
  } = useDetailViewContext();

  const boxStyles = useMemo(() => {
    //  width='1415' height='977'
    const scale1CanvasWidth = 1415;
    const scale1CanvasHeight = 977;

    // when the width is smaller than 900, the panel moves to bottom, and canvas fills all the horizontal space.
    // when height is smaller than 600, then the panel size shrinks
    const canvasWidth = 
      windowSize.width <= 900
        ? windowSize.width 
        : windowSize.height <= 600 
          ? windowSize.width - 380 
          : windowSize.width - 442;
    const canvasHeight = 
      windowSize.width <= 900 
        ? windowSize.height * (advancedModeOn ? 0.4 : 0.6 )
        : windowSize.height;
    const scale = Math.min(
      canvasWidth / scale1CanvasWidth, canvasHeight / scale1CanvasHeight
    );

    const result = {
      expectation: {
        left: canvasWidth * 0.5 - 600 * scale,
        top: canvasHeight * 0.5 - 300 * scale,
        width: 600 * scale,
        height: 550 * scale,
        fontSize: ''
      },
      reality: {
        left: canvasWidth * 0.5 - 320 * scale,
        top: canvasHeight * 0.5 - 350 * scale,
        width: 950 * scale,
        height: 650 * scale,
        fontSize: ''
      }
    };
    result.expectation.fontSize = `${Math.min(result.expectation.width / 20, 18)}px`;
    result.reality.fontSize = result.expectation.fontSize;

    return result;
  }, [windowSize.height, windowSize.width, advancedModeOn]);

  const borderColor = useMemo(() => activeFocusID === FocusPoints.None ? 'black' : 'white', [activeFocusID]);

  const {t} = useTranslation('ai4Jokes');
  const texts = {
    husband: t('3d.husband'),
    wife: t('3d.wife'),
    cat: t('3d.cat'),
    home: t('3d.home'),
    inside: t('3d.inside'),
    happy: t('3d.happy'),
    winner: t('3d.winner'),
    loser: t('3d.loser'),
    forest: t('3d.forest'),
    city: t('3d.city'),
    bridge: t('3d.bridge'),
    expectation: t('3d.expectation'),
    reality: t('3d.reality'),
    car: t('3d.car'),
    sad: t('3d.sad'),
  };

  return (
    <div className='canvas3D'>
      <Canvas gl={{ antialias: false }} dpr={Math.max(window.devicePixelRatio, 2)}>
        <AnimationContextBridge>
          <RenderConditionally doRender={detailViewVisible}/>
          <ForwardEvents/>

          <PerspectiveCamera makeDefault/>
          <BackgroundSphere/>
          <CameraController targets={SectionPositions} mouseMovement={0.4} smoothing={0.9}/>

          <VectorRepresentation
            groupProps={{position: SectionPositions.VectorsOnly.sectionPosition}} 
            texts={texts}
          />
          <SphereSubspaces
            groupProps={{position: SectionPositions.SphereSubspaces.sectionPosition}}  
            texts={texts}
          />
          <TopologicalRelations
            groupProps={{position: SectionPositions.TopologicalRelations.sectionPosition}}  
            texts={texts}
          />
          <DynamicBehavior
            groupProps={{position: SectionPositions.DynamicBehavior.sectionPosition}}  
            texts={texts}
          />
        </AnimationContextBridge>
      </Canvas>

      {
        !isMenuOpen 
          && activeDetailID === DetailSectionIDs.DecodingDynamicBehaviour 
          && <>
            <div
              className={`${styles.border} ${borderColor === 'white' && styles.white} ${showExpectationBox ? styles.show : styles.hide}`}
              style={{ 
                borderColor: borderColor, 
                ...boxStyles.expectation,
                top: '50%'
              }}
            >
              <span style={{
                position: 'absolute',
                top: '-2rem',
                left: 0,
                fontFamily: 'Nexa',
                fontWeight: 900,
                color: borderColor
              }}>{texts.expectation}</span>
            </div>
            <div
              className={`${styles.border} ${borderColor === 'white' && styles.white} ${showRealityBox ? styles.show : styles.hide}`}
              style={{ 
                borderColor: borderColor, 
                ...boxStyles.reality,
                top: '50%'
              }}
            >
              <span style={{
                position: 'absolute',
                top: '-2rem',
                right: 0,
                fontFamily: 'Nexa',
                fontWeight: 900,
                color: borderColor
              }}>{texts.reality}</span>
            </div>
          </>
      }
    </div>
  );
}
